import { EntitySpec, FieldDef } from './Models';
import { COUNTRY_OPTIONS } from './countries';

const G_GENERAL = 'General';
const G_INFO = 'Additional Information';
const G_FINANCIAL = 'Financial';
const G_CHEMISTRY = 'Chemistry';
const G_ENERGY = 'Energy & Power';
const G_PERF = 'Performance';
const G_ELECTRICAL = 'Electrical';
const G_LIFETIME = 'Lifetime';
const G_TEMP = 'Temperature & Environment';
const G_SUBSYSTEMS = 'Other Aspects';
const G_MARKET = 'Market & Cost';
const G_MATURITY = 'Maturity';
const G_COMMENTS = 'Assessment';
const G_AUDIT = 'Sources';

const audit: FieldDef[] = [
  { name: 'txt_source', label: 'Source', type: 'multiline', group: G_AUDIT },
  { name: 'txt_history', label: 'History', type: 'multiline', group: G_AUDIT },
  { name: 'created_by', label: 'Created by', type: 'string', group: G_AUDIT, readOnly: true },
  { name: 'dt_created', label: 'Created at', type: 'datetime', group: G_AUDIT, readOnly: true },
  { name: 'updated_by', label: 'Updated by', type: 'string', group: G_AUDIT, readOnly: true },
  { name: 'dt_updated', label: 'Updated at', type: 'datetime', group: G_AUDIT, readOnly: true },
];

const f = (
  name: string,
  label: string,
  type: FieldDef['type'],
  group?: string,
  extra: Partial<FieldDef> = {},
): FieldDef => ({ name, label, type, group, ...extra });

const fk = (name: string, label: string, fkPath: string, group?: string, required = false): FieldDef => ({
  name,
  label,
  type: 'fk',
  fkPath,
  group,
  required,
});

export const ENTITY_SPECS: EntitySpec[] = [
  // -------------------------------------------------------------------------
  // Suppliers
  // -------------------------------------------------------------------------
  {
    key: 'suppliers',
    label: 'Suppliers',
    singular: 'Supplier',
    path: 'suppliers',
    entityType: 'supplier',
    searchByCountry: true,
    fields: [
      f('name', 'Company name', 'string', G_GENERAL, { required: true }),
      f('logo', 'Logo', 'logo', G_GENERAL),
      f('company_location', 'City/Location', 'string', G_GENERAL),
      f('country', 'Country', 'enum', G_GENERAL, { options: COUNTRY_OPTIONS }),
      f('incorporation_year', 'Incorporation year', 'string', G_GENERAL),
      f('employees', '# Employees', 'string', G_GENERAL, { inlineWithNext: true }),
      f('employees_year', 'Year', 'string', G_GENERAL),
      f('key_managers', 'Key managers', 'multiline', G_GENERAL),
      f('email', 'Contact Email', 'string', G_GENERAL),
      f('url', 'Website', 'string', G_GENERAL),

      f('funds_already_raised', 'Funds already raised', 'multiline', G_FINANCIAL),
      f('funds_to_be_raised', 'Funds to be raised', 'multiline', G_FINANCIAL),

      f('key_customers', 'Company projects & customers', 'multiline', G_INFO),
      f('relation_engie', 'Relation with ENGIE', 'multiline', G_INFO),
      f('interest_in_strat_partner', 'Interest in strategic partner with ENGIE', 'boolean', G_INFO),
      f('further_developments', 'Further developments', 'multiline', G_INFO),
      f('discussions_with_supplier', 'Discussions with supplier', 'multiline', G_INFO),
      f('key_events', 'Key events', 'multiline', G_INFO),

      f('comp_global_assessment', 'Global assessment', 'multiline', G_COMMENTS),
      f('company_innovation', 'Company business &/or innovation', 'multiline', G_COMMENTS),
      f('company_advantages', 'Company advantages', 'multiline', G_COMMENTS),
      f('company_specific_issues', 'Company issues', 'multiline', G_COMMENTS),
      f('trl', 'TRL', 'number', G_COMMENTS),

      f('documents', 'Drop files here - datasheets, reports, anything relevant', 'documents', 'Documents'),

      ...audit,
    ],
    relations: [
      { key: 'cell-products', label: 'Cell Products', targetPath: 'cell-products', fkField: 'supplier_id' },
      { key: 'battery-products', label: 'Module/Rack/Pack', targetPath: 'battery-products', fkField: 'battery_supplier_id' },
      { key: 'solutions', label: 'Solutions', targetPath: 'solutions', fkField: 'battery_product_supplier_id' },
      {
        key: 'target-markets',
        label: 'Target Markets',
        managedPath: (id) => `suppliers/${id}/target-markets`,
        otherPath: 'markets',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Cell Types
  // -------------------------------------------------------------------------
  {
    key: 'cell-types',
    label: 'Cell Types',
    singular: 'Cell Type',
    path: 'cell-types',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
      fk('technology_id', 'Subtechnology', 'technologies', G_GENERAL),
      f('separator', 'Separator', 'string', G_GENERAL),

      fk('anode_id', 'Anode', 'anodes', G_CHEMISTRY),
      fk('cathode_id', 'Cathode', 'cathodes', G_CHEMISTRY),
      fk('electrolyte_id', 'Electrolyte', 'electrolytes', G_CHEMISTRY),

      f('gravimetric_ed_from', 'Gravimetric ED min (Wh/kg)', 'number', G_ENERGY),
      f('gravimetric_ed_to', 'Gravimetric ED max (Wh/kg)', 'number', G_ENERGY),
      f('volumetric_ed_from', 'Volumetric ED min (Wh/L)', 'number', G_ENERGY),
      f('volumetric_ed_to', 'Volumetric ED max (Wh/L)', 'number', G_ENERGY),
      f('gravimetric_p_from', 'Gravimetric P min (W/kg)', 'number', G_ENERGY),
      f('gravimetric_p_to', 'Gravimetric P max (W/kg)', 'number', G_ENERGY),
      f('theoretical_cell_voltage', 'Theoretical cell voltage (V)', 'number', G_ENERGY),

      f('calendar_life_from', 'Calendar life min (years)', 'number', G_LIFETIME),
      f('calendar_life_to', 'Calendar life max (years)', 'number', G_LIFETIME),
      f('cycle_life_from', 'Cycle life min', 'number', G_LIFETIME),
      f('cycle_life_to', 'Cycle life max', 'number', G_LIFETIME),
      f('self_discharge_from', 'Self discharge min (%)', 'number', G_LIFETIME),
      f('self_discharge_to', 'Self discharge max (%)', 'number', G_LIFETIME),

      f('oper_temp_from', 'Operating temp min (°C)', 'number', G_TEMP),
      f('oper_temp_to', 'Operating temp max (°C)', 'number', G_TEMP),
      f('stab_temp', 'Stability temp (°C)', 'number', G_TEMP),

      f('round_trip_eff_dc_from', 'Round trip eff (DC) min (%)', 'number', 'Performance'),
      f('round_trip_eff_dc_to', 'Round trip eff (DC) max (%)', 'number', 'Performance'),

      f('universities_wo', 'Universities working on', 'multiline', G_MARKET),
      f('suppliers_wo', 'Suppliers working on', 'multiline', G_MARKET),
      f('time_to_market', 'Time to market', 'string', G_MARKET),
      f('time_of_market', 'Time of market', 'string', G_MARKET),

      f('general_comment', 'General comment', 'multiline', G_COMMENTS),
      f('advantage', 'Advantages', 'multiline', G_COMMENTS),
      f('remaining_challenges', 'Remaining challenges', 'multiline', G_COMMENTS),
      f('possible_improvements', 'Possible improvements', 'multiline', G_COMMENTS),

      ...audit,
    ],
    relations: [
      { key: 'cell-products', label: 'Cell Products', targetPath: 'cell-products', fkField: 'cell_type_id' },
    ],
  },

  // -------------------------------------------------------------------------
  // Subtechnology (Technology)
  // -------------------------------------------------------------------------
  {
    key: 'technologies',
    label: 'Subtechnologies',
    singular: 'Subtechnology',
    path: 'technologies',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
      fk('technology_type_id', 'Technology', 'technology-types', G_GENERAL),
      f('main_advantages', 'Main advantages', 'multiline', G_COMMENTS),
      f('main_issues', 'Main issues', 'multiline', G_COMMENTS),
      f('report_link', 'Report link', 'string', G_AUDIT),
      ...audit,
    ],
    relations: [
      { key: 'cell-types', label: 'Cell Types', targetPath: 'cell-types', fkField: 'technology_id' },
      { key: 'cell-products', label: 'Cell Products', targetPath: 'cell-products', fkField: 'technology_id' },
      { key: 'battery-products', label: 'Module/Rack/Pack', targetPath: 'battery-products', fkField: 'technology_id' },
      { key: 'solutions', label: 'Solutions', targetPath: 'solutions', fkField: 'sub_technology_id' },
    ],
  },

  // -------------------------------------------------------------------------
  // Technology (TechnologyType)
  // -------------------------------------------------------------------------
  {
    key: 'technology-types',
    label: 'Technologies',
    singular: 'Technology',
    path: 'technology-types',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
      ...audit,
    ],
    relations: [
      { key: 'subtechnologies', label: 'Subtechnologies', targetPath: 'technologies', fkField: 'technology_type_id' },
      { key: 'solutions', label: 'Solutions', targetPath: 'solutions', fkField: 'technology_id' },
    ],
  },

  // -------------------------------------------------------------------------
  // Module/Rack/Pack (BatteryProduct)
  // -------------------------------------------------------------------------
  {
    key: 'battery-products',
    label: 'Module/Rack/Pack',
    singular: 'Module/Rack/Pack',
    path: 'battery-products',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
      fk('battery_supplier_id', 'Battery supplier', 'suppliers', G_GENERAL),
      fk('cell_supplier_id', 'Cell supplier', 'suppliers', G_GENERAL),
      fk('cell_product_id', 'Cell product', 'cell-products', G_GENERAL),
      fk('technology_id', 'Subtechnology', 'technologies', G_GENERAL),

      f('battery_size', 'Battery size', 'string', 'Dimensions'),
      f('battery_weight', 'Battery weight (kg)', 'number', 'Dimensions'),
      f('electrolyte_volume', 'Electrolyte volume (L)', 'number', 'Dimensions'),

      f('nominal_energy', 'Nominal energy (kWh)', 'number', G_ENERGY),
      f('usable_energy', 'Usable energy (kWh)', 'number', G_ENERGY),
      f('capacity', 'Capacity (Ah)', 'number', G_ENERGY),
      f('nominal_power', 'Nominal power (kW)', 'number', G_ENERGY),
      f('energy_efficiency', 'Energy efficiency (%)', 'number', G_ENERGY),

      f('operating_voltage_from', 'Operating voltage min (V)', 'number', G_ELECTRICAL, { inlineWithNext: true }),
      f('operating_voltage_to', 'Operating voltage max (V)', 'number', G_ELECTRICAL),
      f('nominal_voltage', 'Nominal voltage (V)', 'number', G_ELECTRICAL),
      f('max_charge_c_rate', 'Max charge C-rate', 'string', G_ELECTRICAL),
      f('nominal_charge_c_rate', 'Nominal charge C-rate', 'string', G_ELECTRICAL),
      f('max_discharge_c_rate', 'Max discharge C-rate', 'string', G_ELECTRICAL),
      f('nominal_discharge_c_rate', 'Nominal discharge C-rate', 'string', G_ELECTRICAL),
      f('max_charge_discharge_power', 'Max charge/discharge power (kW)', 'number', G_ELECTRICAL),
      f('peak_power', 'Peak power (kW)', 'number', G_ELECTRICAL),
      f('peak_current', 'Peak current (A)', 'number', G_ELECTRICAL),

      f('lifespan', 'Lifespan (years)', 'number', G_LIFETIME, { inlineWithNext: true }),
      f('lifespan_comment', 'Lifespan comment', 'multiline', G_LIFETIME),
      f('cell_cycle_life', 'Cell cycle life', 'number', G_LIFETIME),
      f('battery_cycle_life', 'Battery cycle life', 'number', G_LIFETIME, { inlineWithNext: true }),
      f('battery_cycle_life_comment', 'Battery cycle life comment', 'multiline', G_LIFETIME),

      f('operating_temperature_from', 'Operating temp min (°C)', 'number', G_TEMP, { inlineWithNext: true }),
      f('operating_temperature_to', 'Operating temp max (°C)', 'number', G_TEMP),
      f('operating_temperature_comment', 'Operating temp comment', 'multiline', G_TEMP),
      f('recommended_operating_temperature_from', 'Recommended operating temp min (°C)', 'number', G_TEMP, { inlineWithNext: true }),
      f('recommended_operating_temperature_to', 'Recommended operating temp max (°C)', 'number', G_TEMP),
      f('storage_temperature_from', 'Storage temp min (°C)', 'number', G_TEMP, { inlineWithNext: true }),
      f('storage_temperature_to', 'Storage temp max (°C)', 'number', G_TEMP),
      f('humidity', 'Humidity', 'string', G_TEMP),

      f('bms', 'BMS', 'string', G_SUBSYSTEMS),
      f('bms_comment', 'BMS comment', 'multiline', G_SUBSYSTEMS),
      f('tms', 'TMS', 'string', G_SUBSYSTEMS),
      f('tms_comment', 'TMS comment', 'multiline', G_SUBSYSTEMS),
      f('maintenance', 'Maintenance', 'multiline', G_SUBSYSTEMS),
      f('recycling', 'Recycling', 'multiline', G_SUBSYSTEMS),
      f('safety', 'Safety', 'multiline', G_SUBSYSTEMS),

      f('disponibility', 'Disponibility', 'string', G_MARKET),
      f('market_segment', 'Market segment', 'string', G_MARKET),
      f('time_on_market', 'Time on market', 'string', G_MARKET),
      f('time_to_market', 'Time to market', 'string', G_MARKET),
      f('time_of_market', 'Time of market', 'string', G_MARKET),
      f('warranty', 'Warranty', 'multiline', G_MARKET),

      f('total_capex', 'Total CAPEX (€)', 'string', G_MARKET),

      f('trl', 'TRL', 'number', G_MATURITY),
      f('mrl', 'MRL', 'number', G_MATURITY),
      f('crl', 'CRL', 'number', G_MATURITY),

      f('nda', 'NDA', 'boolean', G_COMMENTS),
      f('tested_by_engie', 'Tested by ENGIE', 'boolean', G_COMMENTS),
      fk('status_id', 'Status', 'statuses', G_COMMENTS),
      f('status_comment', 'Status comment', 'multiline', G_COMMENTS),

      f('comment', 'Comment', 'multiline', G_COMMENTS),
      f('expert_comment', 'Expert comment', 'multiline', G_COMMENTS),

      ...audit,
    ],
    relations: [
      { key: 'solutions', label: 'Solutions', targetPath: 'solutions', fkField: 'battery_product_id' },
    ],
  },

  // -------------------------------------------------------------------------
  // Solutions
  // -------------------------------------------------------------------------
  {
    key: 'solutions',
    label: 'Solutions',
    singular: 'Solution',
    path: 'solutions',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('picture_path', 'Picture', 'picture', G_GENERAL),
      f('solution_size', 'Solution size', 'string', G_GENERAL),
      f('solution_typology', 'Solution typology', 'string', G_GENERAL),
      f('grid_connection', 'Grid connection', 'string', G_GENERAL),
      f('storage_solution_type', 'Technology', 'string', G_GENERAL),
      f('comments', 'Description / Comments', 'multiline', G_GENERAL),

      fk('battery_product_supplier_id', 'Battery product supplier', 'suppliers', 'Product Description'),
      fk('battery_product_id', 'Battery product', 'battery-products', 'Product Description'),
      fk('storage_solution_integrator_id', 'Storage solution integrator', 'suppliers', 'Product Description'),
      fk('inverter_supplier_id', 'Inverter supplier', 'suppliers', 'Product Description'),
      fk('ems_supplier_id', 'EMS supplier', 'suppliers', 'Product Description'),
      fk('technology_id', 'Technology', 'technology-types', 'Product Description'),
      fk('sub_technology_id', 'Subtechnology', 'technologies', 'Product Description'),
      f('technology_type', 'Technology type (free text)', 'string', 'Product Description'),
      f('other_equipement_required', 'Other equipment required', 'multiline', 'Product Description'),
      f('other_equipment_suppliers', 'Other equipment suppliers', 'multiline', 'Product Description'),

      f('solution_cycle_life', 'Solution cycle life', 'number', G_LIFETIME),
      f('solution_calendar_life', 'Solution calendar life (years)', 'number', G_LIFETIME),
      f('battery_eol_percentage', 'Battery EOL (%)', 'number', G_LIFETIME),

      f('capacity_installed', 'Installed capacity (kWh)', 'number', 'Energy'),
      f('capacity_useful_bol', 'Useful capacity BOL (kWh)', 'number', 'Energy'),
      f('scalable', 'Scalable', 'boolean', 'Energy'),
      f('specific_energy', 'Specific energy (Wh/kg)', 'number', 'Energy', { decimals: 2 }),
      f('energy_density', 'Energy density (Wh/L)', 'number', 'Energy', { decimals: 2 }),
      f('battery_nominal_voltage', 'Battery nominal voltage (V)', 'number', G_ELECTRICAL),

      f('number_of_phases', 'Number of phases', 'string', G_ELECTRICAL),
      f('nominal_charge_current', 'Nominal charge current (A)', 'number', G_ELECTRICAL),
      f('nominal_discharge_current', 'Nominal discharge current (A)', 'number', G_ELECTRICAL),
      f('grid_nominal_ac_power', 'Grid nominal AC power (kW)', 'number', G_ELECTRICAL),
      f('nominal_charge_power', 'Nominal charge power (kW)', 'number', G_ELECTRICAL),
      f('nominal_discharge_power', 'Nominal discharge power (kW)', 'number', G_ELECTRICAL),
      f('peak_discharge_power', 'Peak discharge power (kW)', 'number', G_ELECTRICAL),
      f('peak_discharge_time', 'Peak discharge time (s)', 'number', G_ELECTRICAL),
      f('standby_power', 'Standby power (W)', 'number', G_ELECTRICAL),

      f('pv_nominal_pv_power', 'PV nominal power (kWp)', 'number', G_ELECTRICAL),
      f('pv_number_of_mpp_trackers', '# MPP trackers', 'number', G_ELECTRICAL),
      f('pv_voltage_range_mpp_from', 'MPP voltage min (V)', 'number', G_ELECTRICAL),
      f('pv_voltage_range_mpp_to', 'MPP voltage max (V)', 'number', G_ELECTRICAL),

      f('solution_total_footprint', 'Total footprint (m²)', 'string', 'Dimensions'),
      f('solution_total_weight', 'Total weight (kg)', 'number', 'Dimensions'),
      f('solution_total_volume', 'Total volume (m³)', 'number', 'Dimensions'),
      f('solution_operating_temperature_from', 'Operating temp min (°C)', 'number', G_TEMP, { inlineWithNext: true }),
      f('solution_operating_temperature_to', 'Operating temp max (°C)', 'number', G_TEMP),
      f('solution_cooling_strategy', 'Cooling strategy', 'string', G_TEMP),
      f('ip_rating', 'IP rating', 'string', G_TEMP),

      f('dc_efficiency', 'DC efficiency (%)', 'number', 'Efficiency'),
      f('ac_efficiency', 'AC efficiency (%)', 'number', 'Efficiency'),
      f('inverter_efficiency', 'Inverter efficiency (%)', 'number', 'Efficiency'),
      f('specifications_comment', 'Comment', 'multiline', 'Dimensions'),

      f('inverter_compatibility', 'Inverter compatibility', 'multiline', 'Functionalities'),
      f('load_mgmt_smart_home', 'Smart home load mgmt', 'boolean', 'Functionalities', { inlineWithNext: true }),
      f('load_mgmt_smart_home_comment', 'Smart home comment', 'multiline', 'Functionalities'),
      f('func_backup', 'Backup function', 'boolean', 'Functionalities'),
      f('func_backup_phases', 'Backup phases', 'string', 'Functionalities'),
      f('func_backup_power', 'Backup power (kW)', 'number', 'Functionalities'),
      f('func_backup_transfer_time', 'Backup transfer time (ms)', 'number', 'Functionalities'),
      f('ups', 'UPS', 'boolean', 'Functionalities'),
      f('dynamic_feed_in_limiter', 'Dynamic feed-in limiter', 'boolean', 'Functionalities'),
      f('forecasting', 'Forecasting', 'boolean', 'Functionalities'),
      f('externally_controllable', 'Externally controllable', 'boolean', 'Functionalities'),
      f('communication_interface', 'Communication interface', 'string', 'Functionalities'),
      f('other_functionalities', 'Other functionalities', 'multiline', 'Functionalities', { inlineWithNext: true }),
      f('func_comments', 'Functionalities comments', 'multiline', 'Functionalities'),

      f('certification_comments', 'Certification comments', 'multiline', 'Certifications'),
      f('kfw_certified', 'KfW certified', 'boolean', 'Certifications'),
      f('standard_solution_warranty', 'Standard warranty', 'string', 'Certifications'),
      f('systems_installed', 'Systems installed', 'string', 'Certifications'),
      f('performance_warranty_years', 'Perf. warranty (years)', 'number', 'Certifications'),
      f('performance_warranty_cycles', 'Perf. warranty (cycles)', 'number', 'Certifications'),
      f('performance_warranty_percentage', 'Perf. warranty (%)', 'number', 'Certifications'),

      f('time_on_market', 'Time on market', 'string', G_MARKET),
      f('time_to_market', 'Time to market', 'string', G_MARKET),
      f('time_of_market', 'Time of market', 'string', G_MARKET),

      f('total_capex', 'Total CAPEX (€)', 'number', G_MARKET),
      f('total_capex_per_kwh', 'Total CAPEX (€/kWh)', 'number', G_MARKET),
      f('lcos_warrented_cycles', 'LCOS warranted cycles', 'number', G_MARKET),
      f('lcos_warrented_years', 'LCOS warranted years', 'number', G_MARKET),
      f('additional_capex_backup', 'Backup additional CAPEX', 'number', G_MARKET),
      f('date_capex', 'CAPEX date', 'string', G_MARKET),
      f('capex_comment', 'CAPEX comment', 'multiline', G_MARKET),

      f('nda', 'NDA', 'boolean', G_COMMENTS),
      f('tested_by_engie', 'Tested by ENGIE', 'boolean', G_COMMENTS),
      fk('status_id', 'Status', 'statuses', G_COMMENTS),
      f('status_comment', 'Status comment', 'multiline', G_COMMENTS),

      f('general_comments', 'General comments', 'multiline', G_COMMENTS),
      f('expert_opinion', 'Expert opinion', 'multiline', G_COMMENTS),

      ...audit,
    ],
    relations: [
      {
        key: 'certifications',
        label: 'Certifications',
        joinPath: 'solution-certifications',
        joinSelfField: 'solution_id',
        joinOtherField: 'certification_id',
        otherPath: 'certifications',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // CellProduct
  // -------------------------------------------------------------------------
  {
    key: 'cell-products',
    label: 'Cell Products',
    singular: 'Cell Product',
    path: 'cell-products',
    entityType: 'cell_product',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      fk('supplier_id', 'Supplier', 'suppliers', G_GENERAL),
      fk('technology_id', 'Subtechnology', 'technologies', G_GENERAL),
      f('technology_comment', 'Technology comment', 'multiline', G_GENERAL),
      f('cell_size', 'Cell size', 'string', G_GENERAL),
      f('cell_format', 'Cell format', 'string', G_GENERAL),
      f('announcement', 'Announcement', 'boolean', G_GENERAL),
      f('creation_date', 'Creation date', 'string', G_GENERAL),
      f('innovation_inventor', 'Innovation inventor', 'multiline', G_GENERAL),

      fk('cell_type_id', 'Cell type', 'cell-types', G_CHEMISTRY),
      f('cell_type_comment', 'Cell type comment', 'multiline', G_CHEMISTRY),
      fk('cathode_id', 'Cathode', 'cathodes', G_CHEMISTRY),
      f('cathode_comment', 'Cathode comment', 'multiline', G_CHEMISTRY),
      fk('anode_id', 'Anode', 'anodes', G_CHEMISTRY),
      f('anode_comment', 'Anode comment', 'multiline', G_CHEMISTRY),
      fk('electrolyte_id', 'Electrolyte', 'electrolytes', G_CHEMISTRY),
      f('electrolyte_comment', 'Electrolyte comment', 'multiline', G_CHEMISTRY),
      f('separator', 'Separator', 'boolean', G_CHEMISTRY),
      f('separator_comment', 'Separator comment', 'multiline', G_CHEMISTRY),

      f('gravimetric_energy_density', 'Gravimetric Energy Density (Wh/kg)', 'number', G_PERF),
      f('volumetric_energy_density', 'Volumetric Energy Density (Wh/L)', 'number', G_PERF),
      f('gravimetric_power_density', 'Gravimetric Power Density (W/kg)', 'number', G_PERF),
      f('nominal_voltage', 'Nominal voltage (V)', 'number', G_PERF),

      f('calendar_life', 'Calendar life (years)', 'range', G_PERF, {
        range: { from: 'calendar_life_from', to: 'calendar_life_to', comment: 'calendar_life_comment' },
      }),
      f('cycle_life', 'Cycle life', 'range', G_PERF, {
        range: { from: 'cycle_life_from', to: 'cycle_life_to', comment: 'cycle_life_comment' },
      }),
      f('shelf_life', 'Shelf life (years)', 'range', G_PERF, {
        range: { from: 'shelf_life_from', to: 'shelf_life_to', comment: 'shelf_life_comment' },
      }),
      f('self_discharge', 'Self discharge (%)', 'range', G_PERF, {
        range: { from: 'self_discharge_from', to: 'self_discharge_to', comment: 'self_discharge_comment' },
      }),
      f('round_trip_eff_dc', 'Round trip eff DC (%)', 'range', G_PERF, {
        range: { from: 'round_trip_eff_dc_from', to: 'round_trip_eff_dc_to', comment: 'round_trip_eff_dc_comment' },
      }),

      f('operating_temp', 'Operating temp', 'string', 'Quality'),
      f('stability_temp', 'Stability temp', 'string', 'Quality'),

      f('cycle_number_graphic', 'Cycle number graphic', 'string', G_PERF),
      f('calendar_life_graphic', 'Calendar life graphic', 'string', G_PERF),
      f('round_trip_eff_graphic', 'Round trip eff graphic', 'string', G_PERF),
      f('available_energy_graphic', 'Available energy graphic', 'string', G_PERF),

      f('safety', 'Safety', 'multiline', 'Quality'),
      f('material_availability', 'Material availability', 'multiline', 'Quality'),
      f('recycling', 'Recycling', 'multiline', 'Quality'),

      f('time_to_market', 'Time to market', 'string', G_MARKET),
      f('time_of_market', 'Time of market', 'string', G_MARKET),
      f('prod_road_map', 'Production roadmap', 'multiline', G_MARKET),
      f('bill_of_material', 'Bill of material', 'multiline', G_MARKET),

      f('dc_capex_i', 'DC CAPEX (initial)', 'string', G_MARKET),
      f('dc_capex_t', 'DC CAPEX (target)', 'string', G_MARKET),
      f('ac_capex', 'AC CAPEX', 'string', G_MARKET),
      f('lcos', 'LCOS', 'string', G_MARKET),

      f('trl', 'TRL', 'number', G_MATURITY),
      f('mrl', 'MRL', 'number', G_MATURITY),
      f('crl', 'CRL', 'number', G_MATURITY),

      f('tested_by_lbe', 'Tested by LBE', 'boolean', G_COMMENTS),
      f('nda', 'NDA', 'boolean', G_COMMENTS),
      fk('status_id', 'Status', 'cell-product-statuses', G_COMMENTS),
      f('status_comment', 'Status comment', 'multiline', G_COMMENTS),

      f('general_comment', 'General comment', 'multiline', G_COMMENTS),
      f('advantage', 'Advantage', 'multiline', G_COMMENTS),
      f('remaining_challenges', 'Remaining challenges', 'multiline', G_COMMENTS),
      f('expert_comment', 'Expert comment', 'multiline', G_COMMENTS),

      // Free-form documents section (use case B): any number of files attached
      // directly to the cell product, not bound to a specific field.
      f('documents', 'Drop files here - datasheets, test reports, anything relevant', 'documents', 'Documents'),

      ...audit,
    ],
    relations: [
      { key: 'battery-products', label: 'Module/Rack/Pack', targetPath: 'battery-products', fkField: 'cell_product_id' },
    ],
  },

  // -------------------------------------------------------------------------
  // Lookups (still reachable via FK editors / direct URL)
  // -------------------------------------------------------------------------
  {
    key: 'markets',
    label: 'Markets',
    singular: 'Market',
    path: 'markets',
    fields: [f('name', 'Name', 'string', G_GENERAL, { required: true }), ...audit],
  },
  {
    key: 'statuses',
    label: 'Statuses',
    singular: 'Status',
    path: 'statuses',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
    ],
  },
  {
    key: 'cell-product-statuses',
    label: 'Cell Product Statuses',
    singular: 'Cell Product Status',
    path: 'cell-product-statuses',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('description', 'Description', 'multiline', G_GENERAL),
    ],
  },
  {
    key: 'anodes',
    label: 'Anodes',
    singular: 'Anode',
    path: 'anodes',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('material', 'Material', 'string', G_GENERAL),
      f('characteristics', 'Characteristics', 'multiline', G_GENERAL),
      f('active_element_capacity', 'Active element capacity', 'number', G_GENERAL),
      f('voltage', 'Voltage (V)', 'number', G_GENERAL),
      f('advantage', 'Advantage', 'multiline', G_COMMENTS),
      f('main_problems', 'Main problems', 'multiline', G_COMMENTS),
      ...audit,
    ],
  },
  {
    key: 'cathodes',
    label: 'Cathodes',
    singular: 'Cathode',
    path: 'cathodes',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('material', 'Material', 'string', G_GENERAL),
      f('characteristics', 'Characteristics', 'multiline', G_GENERAL),
      f('active_element_capacity', 'Active element capacity', 'number', G_GENERAL),
      f('voltage', 'Voltage (V)', 'number', G_GENERAL),
      f('advantage', 'Advantage', 'multiline', G_COMMENTS),
      f('main_problems', 'Main problems', 'multiline', G_COMMENTS),
      ...audit,
    ],
  },
  {
    key: 'electrolytes',
    label: 'Electrolytes',
    singular: 'Electrolyte',
    path: 'electrolytes',
    fields: [
      f('name', 'Name', 'string', G_GENERAL, { required: true }),
      f('nature', 'Nature', 'string', G_GENERAL),
      f('composition', 'Composition', 'multiline', G_GENERAL),
      f('conductivity', 'Conductivity', 'string', G_GENERAL),
      f('operating_temp_from', 'Operating temp min (°C)', 'number', G_GENERAL, { inlineWithNext: true }),
      f('operating_temp_to', 'Operating temp max (°C)', 'number', G_GENERAL),
      f('advantage', 'Advantage', 'multiline', G_COMMENTS),
      f('main_problems', 'Main problems', 'multiline', G_COMMENTS),
      ...audit,
    ],
  },
  {
    key: 'certifications',
    label: 'Certifications',
    singular: 'Certification',
    path: 'certifications',
    fields: [f('name', 'Name', 'string', G_GENERAL, { required: true }), ...audit],
  },
  {
    key: 'solution-certifications',
    label: 'Solution ↔ Certifications',
    singular: 'Solution Certification',
    path: 'solution-certifications',
    fields: [
      fk('solution_id', 'Solution', 'solutions', G_GENERAL, true),
      fk('certification_id', 'Certification', 'certifications', G_GENERAL, true),
      ...audit,
    ],
  },
];

export const findEntitySpec = (key: string) => ENTITY_SPECS.find((s) => s.key === key);
